import { useMutation } from "@apollo/react-hooks";
import _ from "lodash";
import omit from "lodash/omit";
import { useMemo, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    Text,
} from "@/ui";
import {
    convertMilitaryTimeToAmPm,
    formatUpdateOrderInput,
    getColorReservationMode,
    getDateRangeMessage,
} from "@/utils";
import { useMeta, useNotification } from "@/hooks";
import { useStoreContext } from "context";
import { MUTATION_UPDATE_STORE } from "../../apollo/module-operations/store";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";
import ClosureForm from "./ClosureForm";
import SchedulesFormJSON from "./SchedulesFormJSON";
import {
    PlusIcon,
    PencilIcon,
    RowActionsMenu,
    StoresProviderBadges,
    legacyColorToBadge,
    storeTableUiStyles,
} from "./shared/index";
import closuresStyles from "./Closures.module.scss";

function ClosingPeriodCell({ row }) {
    const dateLabel = getDateRangeMessage(row.start_date, row.end_date);

    if (row.all_day) {
        return (
            <div className={closuresStyles.closingPeriod}>
                <div className={closuresStyles.closingPeriodDate}>
                    {dateLabel}
                </div>
                <Badge className={closuresStyles.timeBadge} variant="neutral">
                    ALL DAY
                </Badge>
            </div>
        );
    }

    return (
        <div className={closuresStyles.closingPeriod}>
            <div className={closuresStyles.closingPeriodDate}>{dateLabel}</div>
            <div className={closuresStyles.closingPeriodTimes}>
                <Badge className={closuresStyles.timeBadge} variant="neutral">
                    {`START: ${convertMilitaryTimeToAmPm(row.time_start)}`}
                </Badge>
                <Badge className={closuresStyles.timeBadge} variant="neutral">
                    {`END: ${convertMilitaryTimeToAmPm(row.time_end)}`}
                </Badge>
            </div>
        </div>
    );
}

const Closures = ({ inputs, setInputs, loading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const { providers } = useStoreContext();
    const [showForm, setShowForm] = useState(false);
    const [showFormJson, setShowFormJson] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [mutate] = useMutation(MUTATION_UPDATE_STORE, {
        refetchQueries: [],
        onCompleted: resp => {
            const input = _.cloneDeep(resp.updateStore);

            setInputs(input);
            handleCancel();
            setRowSelect(null);
            setShowDeleteConfirm(false);
            setIsDelete(false);
            notify(
                `Closures Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  Closures, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
        setShowFormJson(false);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setIsDelete(false);
        setRowSelect(null);
    };

    const confirmDelete = () => {
        const newClosures = _.filter(
            inputs.closures,
            (_item, index) => index !== rowSelect,
        );

        const updateInputs = {
            ...omit(
                {
                    ...inputs,
                    closures: newClosures,
                },
                "ignore_whitespace",
            ),
        };

        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput(updateInputs),
            },
        }).catch(() => {
            setIsUpdating(false);
        });
    };

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput({
                    ...omit(
                        {
                            ...inputs,
                            closures: array,
                        },
                        "ignore_whitespace",
                    ),
                }),
            },
        });
    };

    const handlerUpdatingOrCreateJSON = nextInputs => {
        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput(nextInputs),
            },
        });
    };

    const closures = (inputs && inputs.closures) || [];

    const rows = useMemo(
        () =>
            closures.map((closure, index) => ({
                ...closure,
                _index: index,
            })),
        [closures],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm || showFormJson}
                            label={row.name || `closure ${row._index + 1}`}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 160,
                renderCell: row => row.name || "",
            },
            {
                id: "reservation_mode",
                header: "Reservation Mode",
                minWidth: 160,
                renderCell: row => (
                    <Badge
                        variant={legacyColorToBadge(
                            getColorReservationMode(row.reservation_mode) ||
                                "info",
                        )}
                    >
                        {row.reservation_mode}
                    </Badge>
                ),
            },
            {
                id: "start_date",
                header: "Closing Period",
                minWidth: 220,
                renderCell: row => <ClosingPeriodCell row={row} />,
            },
            {
                id: "stores_provider_ids",
                header: "Stores Provider",
                minWidth: 200,
                renderCell: row => (
                    <StoresProviderBadges value={row.stores_provider_ids} />
                ),
            },
        ],
        [isUpdating, showForm, showFormJson],
    );

    const toolbarDisabled = isUpdating || showFormJson || showForm;

    return (
        <>
            <Modal
                description="Are you sure you want to remove this closure?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={closeDeleteConfirm}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) closeDeleteConfirm();
                }}
                open={showDeleteConfirm}
                title="Remove closure"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Closures</Heading>
                            <Text tone="secondary" variant="body-md">
                                Closures Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add",
                                    label: "Add Closure",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "json",
                                    label: "Update Form JSON",
                                    icon: <PencilIcon />,
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowFormJson(true);
                                    },
                                },
                            ]}
                            aria-label="Closures actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {showForm ? (
                    <ClosureForm
                        closures={inputs.closures}
                        handleCancel={handleCancel}
                        handlerUpdatingOrCreate={handlerUpdatingOrCreate}
                        isCreate={isCreate}
                        isUpdating={isUpdating}
                        selectClosure={rowSelect}
                        stores={providers}
                    />
                ) : null}

                {showFormJson ? (
                    <SchedulesFormJSON
                        context="Closures"
                        handleCancel={handleCancel}
                        handlerUpdatingOrCreate={handlerUpdatingOrCreateJSON}
                        inputs={inputs}
                        isUpdating={isUpdating}
                        setInputs={setInputs}
                    />
                ) : null}

                <FormLayout>
                    <FormLayout.Section title="Closures">
                        {loading ? (
                            <div className={styles.loading}>
                                <LoadingProgress
                                    indeterminate
                                    indicator="circular"
                                    size="sm"
                                    title="Loading"
                                />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        )}
                    </FormLayout.Section>
                </FormLayout>
            </Panel>
        </>
    );
};

export default Closures;
